import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useEffect, useState } from 'react';

export function useSafeArea() {
  const isAndroid = Capacitor.getPlatform() === 'android';
  const isIOS = Capacitor.getPlatform() === 'ios';
  const [statusBarHeight, setStatusBarHeight] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isIPhone16OrDynamicIslandDevice, setIsIPhone16OrDynamicIslandDevice] =
    useState(false);
  const [isOlderOrSEDevice, setIsOlderOrSEDevice] = useState(false);
  const [deviceModel, setDeviceModel] = useState('');
  const [safeAreaValues, setSafeAreaValues] = useState({
    top: '0px',
    bottom: '0px',
  });

  // Detect device type
  useEffect(() => {
    if (isIOS) {
      // Check for iPhone 16 or similar models with Dynamic Island
      const { width, height } = window.screen;
      const maxDimension = Math.max(width, height);
      const pixelRatio = window.devicePixelRatio || 2;

      // Get device model from user agent if possible
      const userAgent = navigator.userAgent;
      let model = '';

      if (userAgent.includes('iPhone')) {
        // Try to extract iPhone model
        const modelMatch = userAgent.match(/iPhone(\d+),/);
        if (modelMatch && modelMatch[1]) {
          model = `iPhone ${modelMatch[1]}`;
        }
      }

      setDeviceModel(model);

      // iPhone 15/16/14 Pro have high-res screens and specific dimensions
      // More precise detection for newer devices
      if (
        (pixelRatio >= 3 && maxDimension >= 844) ||
        userAgent.includes('iPhone14,') || // iPhone 14 Pro models
        userAgent.includes('iPhone15,') || // iPhone 15 models
        userAgent.includes('iPhone16,')
      ) {
        // iPhone 16 models
        setIsIPhone16OrDynamicIslandDevice(true);
        console.log('Detected Dynamic Island device (iPhone 14 Pro/15/16)', {
          model,
          pixelRatio,
          maxDimension,
        });
      } else if (
        maxDimension < 812 ||
        userAgent.includes('iPhone8,4') ||
        userAgent.includes('iPhone SE')
      ) {
        // Detect older iPhones or SE models (iPhone SE, iPhone 8, etc)
        setIsOlderOrSEDevice(true);
        console.log('Detected older iPhone or SE device', {
          model,
          pixelRatio,
          maxDimension,
        });
      }
    }
  }, [isIOS]);

  // Initialize safe areas
  useEffect(() => {
    if (isInitialized) return;

    const initSafeArea = async () => {
      // Set default values first
      addCssVariablesToDocument('--normalized-safe-area-top', '0px');
      addCssVariablesToDocument('--safe-area-inset-bottom', '0px');
      addCssVariablesToDocument('--safe-area-inset-left', '0px');
      addCssVariablesToDocument('--safe-area-inset-right', '0px');

      if (isAndroid) {
        try {
          // For Android, always set a fixed status bar height as the API is unreliable
          const statusBarHeight = 10;
          setStatusBarHeight(statusBarHeight);
          addCssVariablesToDocument(
            '--normalized-safe-area-top',
            `${statusBarHeight}px`
          );

          // Store the value for direct access
          setSafeAreaValues((prev) => ({
            ...prev,
            top: `${statusBarHeight}px`,
          }));

          // Also try to use StatusBar overlay to ensure content can go edge-to-edge
          await StatusBar.setOverlaysWebView({ overlay: true });
          await StatusBar.setStyle({ style: Style.Dark });
        } catch (error) {
          console.error('Error setting up StatusBar:', error);
        }
      } else if (isIOS) {
        // For iOS, we need to normalize immediately for first render
        normalizeIOSSafeAreas();

        // Also set up a listener for when DOM is fully loaded
        document.addEventListener('DOMContentLoaded', () => {
          // Small delay to ensure CSS env() values are ready
          setTimeout(() => {
            normalizeIOSSafeAreas();
          }, 300);
        });
      }

      setIsInitialized(true);
    };

    initSafeArea();
  }, [
    isAndroid,
    isIOS,
    isInitialized,
    isIPhone16OrDynamicIslandDevice,
    isOlderOrSEDevice,
    deviceModel,
  ]);

  // Apply iOS safe areas when device type is determined
  useEffect(() => {
    if (isIOS) {
      normalizeIOSSafeAreas();
    }
  }, [isIOS, isOlderOrSEDevice, isIPhone16OrDynamicIslandDevice, deviceModel]);

  // Helper functions
  const normalizeIOSSafeAreas = () => {
    try {
      // Get a computed style of a temporary element to extract safe area values
      const tempEl = document.createElement('div');
      tempEl.style.paddingTop = 'env(safe-area-inset-top)';
      tempEl.style.paddingBottom = 'env(safe-area-inset-bottom)';
      document.body.appendChild(tempEl);
      const computedStyle = getComputedStyle(tempEl);
      const paddingTop = parseInt(computedStyle.paddingTop) || 0;
      const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
      document.body.removeChild(tempEl);

      console.log(
        'Detected safe areas - top:',
        paddingTop,
        'bottom:',
        paddingBottom,
        'Device:',
        deviceModel
      );

      // Add a special margin for older iOS devices to fix layout issues
      if (isOlderOrSEDevice) {
        addCssVariablesToDocument(
          '--legacy-ios-margin-top-adjustment',
          '-4px'
        );
      } else {
        addCssVariablesToDocument('--legacy-ios-margin-top-adjustment', '0px');
      }

      let topValue;

      // Special handling for iPhone 16 and similar devices with Dynamic Island
      if (isIPhone16OrDynamicIslandDevice) {
        if (paddingTop > 40) {
          // Dynamic Island devices with large notch (iPhone 14 Pro, 15 Pro, 16 Pro)
          topValue = '47px';
        } else {
          // Other Dynamic Island devices
          topValue = '40px';
        }
      } else if (isOlderOrSEDevice) {
        // For iPhone SE and similar devices with home button
        topValue = '20px';
      } else {
        // For regular iPhones with notch (non-Dynamic Island)
        topValue = '35px';
      }

      console.log(
        'Applied safe area top value:',
        topValue,
        'for device:',
        deviceModel
      );

      // Set CSS variables and also store the values for direct access
      addCssVariablesToDocument('--normalized-safe-area-top', topValue);
      setSafeAreaValues((prev) => ({ ...prev, top: topValue }));

      // For iPhone SE, explicitly set bottom to 0
      if (isOlderOrSEDevice) {
        addCssVariablesToDocument('--normalized-safe-area-bottom', '0px');
        setSafeAreaValues((prev) => ({ ...prev, bottom: '0px' }));
      } else {
        // For other iOS devices, use actual bottom values
        const bottomValue = `${paddingBottom}px`;
        addCssVariablesToDocument('--normalized-safe-area-bottom', bottomValue);
        setSafeAreaValues((prev) => ({ ...prev, bottom: bottomValue }));
      }
    } catch (error) {
      console.error('Error normalizing iOS safe areas:', error);
      // Fallback to standard values if detection fails
      addCssVariablesToDocument('--normalized-safe-area-top', '28px');
      setSafeAreaValues((prev) => ({ ...prev, top: '28px' }));

      if (isOlderOrSEDevice) {
        addCssVariablesToDocument('--normalized-safe-area-bottom', '0px');
        setSafeAreaValues((prev) => ({ ...prev, bottom: '0px' }));
      }
    }
  };



  const addCssVariablesToDocument = (name: string, value: string) => {
    document.documentElement.style.setProperty(name, value);
  };

  // Public methods to expose
  const getSafeAreaCss = () => {
    // Return direct values that were calculated and stored
    return safeAreaValues;
  };

  const getStatusBarHeight = () => statusBarHeight;

  // Return the public API
  return {
    getSafeAreaCss,
    getStatusBarHeight,
    isAndroid,
    isIOS,
    isIPhone16OrDynamicIslandDevice,
    isOlderOrSEDevice,
    deviceModel,
  };
}
